import { IntlZoneProvider, MS_PER_DAY, MS_PER_SECOND } from './zone.js';
export { MS_PER_SECOND, MS_PER_HOUR, MS_PER_DAY, UnknownTimeZoneError } from './zone.js';
/** The process-wide default provider (runtime Intl). */
export const intlZoneProvider = new IntlZoneProvider();
let provider = intlZoneProvider;
/** Installs `p` as the provider for all engine time math; `null` restores the Intl default. */
export function setZoneProvider(p) {
    provider = p ?? intlZoneProvider;
}
export function getZoneProvider() {
    return provider;
}
/** Runs `fn` with `p` installed, restoring the previous provider afterwards. */
export function withZoneProvider(p, fn) {
    const prev = provider;
    provider = p;
    try {
        return fn();
    }
    finally {
        provider = prev;
    }
}
/** True when the provider knows `zone` (an IANA name; offsets like `+01:00` are rejected). */
export function isKnownZone(zone) {
    return provider.isKnownZone(zone);
}
/** UTC offset (ms) of `zone` at `instantMs`. */
export function offsetAt(instantMs, zone) {
    return provider.offsetAt(instantMs, zone);
}
/** Wall-clock value (naive ms) of `instantMs` in `zone`. */
export function wallClock(instantMs, zone) {
    return instantMs + provider.offsetAt(instantMs, zone);
}
/** Every instant whose wall clock in `zone` equals `naive` (0 in a gap, 2 in an overlap), ascending. */
export function localToUtcAll(naive, zone) {
    return provider.validOffsets(naive, zone).map((o) => naive - o);
}
/**
 * The first instant whose wall clock in `zone` is ≥ `naive`:
 * - exists once  → that instant;
 * - overlap      → the earlier instant (first repetition);
 * - gap          → the instant of the transition (first instant after the gap).
 */
export function localToUtc(naive, zone) {
    const exact = localToUtcAll(naive, zone);
    if (exact.length > 0)
        return exact[0];
    const offs = [offsetAt(naive - MS_PER_DAY, zone), offsetAt(naive, zone), offsetAt(naive + MS_PER_DAY, zone)];
    let lo = naive - Math.max(...offs);
    let hi = naive - Math.min(...offs);
    if (!(wallClock(lo, zone) < naive) || !(wallClock(hi, zone) >= naive)) {
        return naive - offsetAt(naive, zone); // unreachable under the module assumption
    }
    while (hi - lo > 1) {
        const mid = lo + Math.floor((hi - lo) / 2);
        if (wallClock(mid, zone) >= naive)
            hi = mid;
        else
            lo = mid;
    }
    return hi;
}
/** Offset transitions of `zone` in the half-open range (fromMs, toMs], ascending. */
export function zoneTransitions(zone, fromMs, toMs) {
    if (provider.transitionsBetween)
        return provider.transitionsBetween(zone, fromMs, toMs);
    const out = [];
    let t = fromMs;
    for (;;) {
        const n = provider.nextTransition(zone, t, toMs);
        if (n === null)
            return out;
        out.push(n);
        t = n;
    }
}
/** First offset transition of `zone` in (afterMs, untilMs], or null. */
export function nextZoneTransition(zone, afterMs, untilMs) {
    return provider.nextTransition(zone, afterMs, untilMs);
}
/** Last offset transition of `zone` in (sinceMs, beforeMs], or null. */
export function previousZoneTransition(zone, beforeMs, sinceMs) {
    return provider.previousTransition(zone, beforeMs, sinceMs);
}
// ---------------------------------------------------------------------------------------
// Calendar helpers (pure arithmetic on day numbers, identical in every implementation)
function utcFields(y, mo, d, h = 0, mi = 0, s = 0) {
    const dt = new Date(0);
    dt.setUTCFullYear(y, mo - 1, d);
    dt.setUTCHours(h, mi, s, 0);
    return dt.getTime();
}
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** `YYYY-MM-DD` → day number, or null when malformed / non-existent (e.g. 2026-02-30). */
export function parseDate(s) {
    const m = DATE_RE.exec(s);
    if (!m)
        return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (mo < 1 || mo > 12 || d < 1 || d > 31)
        return null;
    const ms = utcFields(y, mo, d);
    const back = new Date(ms);
    if (back.getUTCFullYear() !== y || back.getUTCMonth() + 1 !== mo || back.getUTCDate() !== d)
        return null;
    return Math.round(ms / MS_PER_DAY);
}
export function formatDay(day) {
    return new Date(day * MS_PER_DAY).toISOString().slice(0, 10);
}
/** ISO weekday index of a day number: 0 = Monday … 6 = Sunday (1970-01-01 was a Thursday). */
export function isoWeekdayIndex(day) {
    return (((day + 3) % 7) + 7) % 7;
}
export function dayOfMonth(day) {
    return new Date(day * MS_PER_DAY).getUTCDate();
}
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;
/** `HH:MM[:SS]` → ms since midnight; `24:00[:00]` only when `allow24`. Null when malformed. */
export function parseTime(s, allow24 = false) {
    const m = TIME_RE.exec(s);
    if (!m)
        return null;
    const h = Number(m[1]);
    const mi = Number(m[2]);
    const sec = m[3] === undefined ? 0 : Number(m[3]);
    if (allow24 && h === 24 && mi === 0 && sec === 0)
        return MS_PER_DAY;
    if (h > 23 || mi > 59 || sec > 59)
        return null;
    return ((h * 60 + mi) * 60 + sec) * MS_PER_SECOND;
}
// ---------------------------------------------------------------------------------------
// Instants
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})$/;
export function toMs(instant) {
    if (typeof instant === 'number') {
        if (!Number.isFinite(instant))
            throw new RangeError('Instant must be finite');
        return Math.floor(instant);
    }
    if (instant instanceof Date)
        return instant.getTime();
    if (!ISO_INSTANT.test(instant))
        throw new RangeError(`Not an ISO-8601 instant with zone designator: ${instant}`);
    const ms = Date.parse(instant);
    if (Number.isNaN(ms))
        throw new RangeError(`Invalid instant: ${instant}`);
    return ms;
}
/** Parses an optional instant field; null/undefined → null. */
export function optMs(v) {
    return v === null || v === undefined ? null : toMs(v);
}
/** Canonical UTC ISO string: `YYYY-MM-DDTHH:MM:SSZ`, with `.sss` only when non-zero. */
export function toIso(ms) {
    const s = new Date(ms).toISOString();
    return s.endsWith('.000Z') ? `${s.slice(0, -5)}Z` : s;
}
/** Wall-clock value → `YYYY-MM-DDTHH:MM:SS(.sss)` (no zone designator). */
export function formatWall(naive) {
    const s = toIso(naive);
    return s.slice(0, -1);
}
//# sourceMappingURL=time.js.map